import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, Briefcase, GraduationCap, Wrench, AlertTriangle } from 'lucide-react';
import { useOccupationEnrichment, type ProjectionsEnrichment } from '@/hooks/useOccupationEnrichment';
import OccupationScoreBadges from './OccupationScoreBadges';

interface OccupationEnrichmentProps {
  soc?: string;
  stateCode?: string;
  occupationTitle?: string;
  /** Notifies the parent whether unique enriched content rendered (for SEO indexing). */
  onEnrichmentLoaded?: (hasEnrichedContent: boolean) => void;
}

const outlookMeta: Record<NonNullable<ProjectionsEnrichment['outlook_band']>, { label: string; className: string; icon: typeof TrendingUp }> = {
  'growing-fast': { label: 'Growing fast', className: 'bg-green-100 text-green-800', icon: TrendingUp },
  growing: { label: 'Growing', className: 'bg-emerald-100 text-emerald-800', icon: TrendingUp },
  stable: { label: 'Stable', className: 'bg-blue-100 text-blue-800', icon: Minus },
  declining: { label: 'Declining', className: 'bg-amber-100 text-amber-800', icon: TrendingDown },
};

const formatNumber = (value?: number | null) =>
  value == null ? '-' : new Intl.NumberFormat('en-US').format(Math.round(value));

const OccupationEnrichment = ({ soc, stateCode, occupationTitle, onEnrichmentLoaded }: OccupationEnrichmentProps) => {
  const { data, isLoading } = useOccupationEnrichment(soc, stateCode);

  useEffect(() => {
    if (data && onEnrichmentLoaded) {
      onEnrichmentLoaded(!!data.has_enriched_content);
    }
  }, [data, onEnrichmentLoaded]);

  // Never emit animate-pulse during prerender — a slow enrichment fetch
  // must not fail validate-prerender. Snapshots hydrate when available.
  if (isLoading) {
    return null;
  }

  if (!data || (!data.onet && !data.projections && !data.scores && (!data.layoffs || data.layoffs.length === 0))) {
    return null;
  }

  const { onet, projections, scores, layoffs } = data;
  const outlook = projections?.outlook_band ? outlookMeta[projections.outlook_band] : null;

  return (
    <div className="space-y-6">
      {/* Report-card scores */}
      {scores && <OccupationScoreBadges scores={scores} />}

      {/* Growth outlook */}
      {projections && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Briefcase className="h-5 w-5 text-wage-primary" />
                Job Outlook {projections.base_year}-{projections.proj_year}
              </CardTitle>
              {outlook && (
                <Badge className={outlook.className}>
                  <outlook.icon className="h-3 w-3 mr-1" />
                  {outlook.label}
                </Badge>
              )}
            </div>
            <CardDescription>
              Official BLS Employment Projections for {occupationTitle || 'this occupation'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-muted-foreground">Projected change</div>
                <div className="text-xl font-bold">
                  {projections.pct_change != null && projections.pct_change > 0 ? '+' : ''}
                  {projections.pct_change ?? '-'}%
                </div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-muted-foreground">Annual openings</div>
                <div className="text-xl font-bold">{formatNumber(projections.annual_openings)}</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-muted-foreground">Projected jobs ({projections.proj_year})</div>
                <div className="text-xl font-bold">{formatNumber(projections.proj_employment)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Skills & what you do (O*NET) */}
      {onet && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wrench className="h-5 w-5 text-wage-primary" />
              Skills & What You Do
            </CardTitle>
            {onet.education_summary && (
              <CardDescription className="flex items-center gap-2">
                <GraduationCap className="h-4 w-4" />
                {onet.education_summary}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {onet.top_skills.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2">Top skills</div>
                <div className="flex flex-wrap gap-2">
                  {onet.top_skills.map((skill) => (
                    <Badge key={skill} variant="secondary">{skill}</Badge>
                  ))}
                </div>
              </div>
            )}
            {onet.top_tasks.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2">Common tasks</div>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  {onet.top_tasks.map((task) => (
                    <li key={task}>{task}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Layoffs signal */}
      {layoffs && layoffs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Recent Layoffs Activity
            </CardTitle>
            <CardDescription>
              Recent WARN Act workforce reductions in industries that employ{' '}
              {occupationTitle ? occupationTitle.toLowerCase() : 'this occupation'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {layoffs.slice(0, 5).map((event, idx) => (
                <li key={`${event.company}-${idx}`} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    {event.source_url ? (
                      <a
                        href={event.source_url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="font-medium hover:underline"
                      >
                        {event.company}
                      </a>
                    ) : (
                      <span className="font-medium">{event.company}</span>
                    )}
                    {event.location && <span className="text-muted-foreground"> - {event.location}</span>}
                    {event.industry && (
                      <div className="text-xs text-muted-foreground">{event.industry}</div>
                    )}
                  </div>
                  <div className="text-right">
                    {event.affected != null && (
                      <div className="font-medium">{formatNumber(event.affected)} affected</div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              Source: state WARN Act filings, updated daily
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OccupationEnrichment;
